/**
 * API Field Mapping Interface
 * 
 * Admin interface for managing field definitions, aliases, and mappings
 * Handles unknown field submission approval and field evolution
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    Tab,
    Tabs,
    IconButton,
    Tooltip,
    Divider,
    Card,
    CardContent,
    Badge,
    Stack,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Link as LinkIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon
} from '@mui/icons-material';

import { apiFieldDefinitionLoader } from '../services/apiFieldDefinitionLoader';
import { apiFieldMappingRepository } from '../repositories/apiFieldMappingRepository';
import type { UnknownFieldSubmission, FieldClassificationRequest } from '../repositories/apiFieldMappingRepository';
import type { ApiFieldDefinition, ApiFieldAlias, ApiFieldMapping } from '../models/apiTypes';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`field-mapping-tabpanel-${index}`}
        aria-labelledby={`field-mapping-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

// UnknownFieldSubmission interface is now imported from the repository

export const ApiFieldMappingInterface: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [fieldDefinitions, setFieldDefinitions] = useState<ApiFieldDefinition[]>([]);
    const [fieldAliases, setFieldAliases] = useState<ApiFieldAlias[]>([]);
    const [unknownFields, setUnknownFields] = useState<UnknownFieldSubmission[]>([]);
    
    // Debug: Log state changes
    useEffect(() => {
        console.log(`🔍 FIELD MAPPING STATE: unknownFields updated, count: ${unknownFields.length}`);
        if (unknownFields.length > 0) {
            console.log(`🔍 FIELD MAPPING STATE: First few fields:`, unknownFields.slice(0, 3).map(f => f.submittedFieldName));
        }
    }, [unknownFields]);
    const [loading, setLoading] = useState(true);
    const [editingField, setEditingField] = useState<ApiFieldDefinition | null>(null);
    const [showNewFieldDialog, setShowNewFieldDialog] = useState(false);
    const [showMappingDialog, setShowMappingDialog] = useState(false);
    const [selectedUnknownField, setSelectedUnknownField] = useState<UnknownFieldSubmission | null>(null);
    
    // Form state for new/editing field
    const [fieldForm, setFieldForm] = useState<Partial<ApiFieldDefinition>>({
        fieldName: '',
        apiFieldName: '',
        displayName: '',
        description: '',
        dataType: 'string',
        publish: true,
        visible: true,
        required: false,
        fieldType: 'variable',
        aliases: [],
        units: null,
        options: null
    });
    
    // Separate state for options text to avoid lag
    const [optionsText, setOptionsText] = useState('');
    
    // Debounced option processing to improve performance
    const processOptionsFromText = useCallback((text: string) => {
        const options = text.split('\n').filter(opt => opt.trim()).map(opt => opt.trim());
        setFieldForm(prev => ({ ...prev, options: options.length > 0 ? options : null }));
    }, []);
    
    // Sync options text when fieldForm.options changes (from loading data)
    useEffect(() => {
        if (fieldForm.options) {
            setOptionsText(fieldForm.options.join('\n'));
        } else {
            setOptionsText('');
        }
    }, [fieldForm.options]);
    
    // Debounced effect to update fieldForm.options from text changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            processOptionsFromText(optionsText);
        }, 300); // 300ms debounce
        
        return () => clearTimeout(timeoutId);
    }, [optionsText, processOptionsFromText]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load field definitions
            const definitions = await apiFieldDefinitionLoader.loadFieldDefinitionsFromFile();
            setFieldDefinitions(definitions);
            
            // Load field definitions into the repository
            apiFieldMappingRepository.loadFieldDefinitions(definitions);

            // Note: Unknown fields are now tracked automatically during API submissions
            // No need to analyze existing quotes on every load

            // Load server-side field definitions and unknown fields
            try {
                const { authService } = await import('../services/authService');
                const token = await authService.getIdToken();
                
                console.log(`🔍 FIELD MAPPING DEBUG: Making request with token: ${token ? `${token.substring(0, 20)}...` : 'NO TOKEN'}`);
                
                // Load unknown fields (no query params)
                const fieldResponse = await fetch('/api/v1/admin/field-definitions', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                console.log(`🔍 FIELD MAPPING DEBUG: Response status: ${fieldResponse.status}`);

                if (fieldResponse.ok) {
                    const fieldData = await fieldResponse.json();
                    const serverUnknownFields = fieldData.unknownFields || [];
                    console.log(`🔍 FIELD MAPPING DEBUG: Server response:`, fieldData);
                    console.log(`🔍 FIELD MAPPING DEBUG: Unknown fields received:`, serverUnknownFields);
                    setUnknownFields(serverUnknownFields);
                    console.log(`📋 FIELD MAPPING: Loaded ${serverUnknownFields.length} unknown fields from server`);
                } else {
                    console.log(`❌ FIELD MAPPING: Server response not OK:`, fieldResponse.status, fieldResponse.statusText);
                    // Fallback to repository
                    const unknownFieldsData = apiFieldMappingRepository.getUnknownFieldsNeedingApproval();
                    setUnknownFields(unknownFieldsData);
                    console.log(`📋 FIELD MAPPING: Using repository fallback - ${unknownFieldsData.length} unknown fields`);
                }

                // Load published field definitions from server (includes dynamically added fields)
                const publishedResponse = await fetch('/api/v1/admin/field-definitions?published=true&visible=true', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (publishedResponse.ok) {
                    const publishedData = await publishedResponse.json();
                    const serverFieldDefinitions = publishedData.data || [];
                    console.log(`📋 FIELD MAPPING: Loaded ${serverFieldDefinitions.length} server field definitions`);
                    
                    // Merge static definitions with server definitions, with server taking precedence
                    const serverFieldNames = new Set(serverFieldDefinitions.map((f: any) => f.apiFieldName));
                    const staticOnlyFields = definitions.filter(f => !serverFieldNames.has(f.apiFieldName));
                    const mergedDefinitions = [...staticOnlyFields, ...serverFieldDefinitions];
                    
                    console.log(`📋 FIELD MAPPING: Merged ${staticOnlyFields.length} static-only + ${serverFieldDefinitions.length} server = ${mergedDefinitions.length} total field definitions`);
                    setFieldDefinitions(mergedDefinitions);
                    apiFieldMappingRepository.loadFieldDefinitions(mergedDefinitions);
                } else {
                    console.log(`❌ FIELD MAPPING: Could not load server field definitions:`, publishedResponse.status);
                }
            } catch (error) {
                console.warn('Could not load unknown fields from server, using repository:', error);
                const unknownFieldsData = apiFieldMappingRepository.getUnknownFieldsNeedingApproval();
                setUnknownFields(unknownFieldsData);
            }

            console.log(`📋 FIELD MAPPING: Loaded ${definitions.length} field definitions`);
        } catch (error) {
            console.error('Failed to load field mapping data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleEditField = (field: ApiFieldDefinition) => {
        setEditingField(field);
        setFieldForm({ ...field });
        setShowNewFieldDialog(true);
    };

    const handleSaveField = async () => {
        try {
            if (editingField) {
                // Update existing field via server API
                const { authService } = await import('../services/authService');
                const token = await authService.getIdToken();
                
                if (token) {
                    const fieldName = editingField.apiFieldName || editingField.fieldName;
                    const response = await fetch(`/api/v1/admin/field-definitions/${fieldName}`, {
                        method: 'PUT',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(fieldForm)
                    });

                    if (response.ok) {
                        const result = await response.json();
                        const updatedField = result.data;
                        
                        // Update local state
                        const updatedFields = fieldDefinitions.map(f => 
                            f.id === editingField.id ? updatedField : f
                        );
                        setFieldDefinitions(updatedFields);
                        
                        // Update in repository
                        apiFieldMappingRepository.loadFieldDefinitions(updatedFields);
                        
                        console.log(`✅ Field "${fieldName}" updated successfully`);
                    } else {
                        const error = await response.json();
                        console.error('Failed to update field:', error.error);
                        throw new Error(error.error);
                    }
                } else {
                    throw new Error('Authentication token not available');
                }
            } else {
                // Create new field via API (handles classification logic)
                if (selectedUnknownField) {
                    // This is creating a field from an unknown field - call server API
                    const { authService } = await import('../services/authService');
                    const token = await authService.getIdToken();
                    
                    if (token) {
                        const classificationRequest = {
                            fieldName: fieldForm.apiFieldName || fieldForm.fieldName || '',
                            fieldType: fieldForm.fieldType || 'variable',
                            displayName: fieldForm.displayName,
                            description: fieldForm.description,
                            units: fieldForm.units,
                            dataType: fieldForm.dataType || 'string',
                            publish: fieldForm.publish !== false,
                            visible: fieldForm.visible !== false
                        };
                        
                        const response = await fetch('/api/v1/admin/classify-field', {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(classificationRequest)
                        });

                        if (response.ok) {
                            const result = await response.json();
                            const newField = result.data;
                            setFieldDefinitions([...fieldDefinitions, newField]);
                            
                            // Remove from unknown fields
                            setUnknownFields(unknownFields.filter(uf => uf.id !== selectedUnknownField.id));
                            setSelectedUnknownField(null);
                            console.log(`✅ Field "${classificationRequest.fieldName}" classified successfully`);
                        } else {
                            const error = await response.json();
                            console.error('Failed to classify field:', error.error);
                            throw new Error(error.error);
                        }
                    } else {
                        throw new Error('Authentication token not available');
                    }
                } else {
                    // Regular new field creation
                    const newField: ApiFieldDefinition = {
                        ...fieldForm,
                        id: Date.now().toString(),
                        aliases: fieldForm.aliases || [],
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        isApproved: true
                    } as ApiFieldDefinition;
                    setFieldDefinitions([...fieldDefinitions, newField]);
                    
                    // Add to repository
                    apiFieldMappingRepository.loadFieldDefinitions([...fieldDefinitions, newField]);
                }
            }
            
            setShowNewFieldDialog(false);
            setEditingField(null);
            resetForm();
        } catch (error) {
            console.error('Failed to save field:', error);
        }
    };

    const resetForm = () => {
        setFieldForm({
            fieldName: '',
            apiFieldName: '',
            displayName: '',
            description: '',
            dataType: 'string',
            publish: true,
            visible: true,
            required: false,
            fieldType: 'variable',
            aliases: [],
            units: null,
            options: null
        });
        setOptionsText('');
    };

    const handleMapUnknownField = (unknownField: UnknownFieldSubmission) => {
        setSelectedUnknownField(unknownField);
        setFieldForm({
            ...fieldForm,
            fieldName: unknownField.submittedFieldName.replace(/_/g, ' '),
            apiFieldName: unknownField.submittedFieldName,
            aliases: [unknownField.submittedFieldName]
        });
        setShowMappingDialog(true);
    };

    const handleCreateNewFieldFromUnknown = async () => {
        if (!selectedUnknownField) return;
        
        // Pre-populate form with unknown field data
        const unknownField = selectedUnknownField;
        
        // Determine if this should be a fixed field based on values
        const suggestedFieldType = unknownField.submittedValues.length > 1 ? 'fixed' : 'variable';
        
        setFieldForm({
            fieldName: unknownField.submittedFieldName.replace(/_/g, ' '),
            apiFieldName: unknownField.submittedFieldName,
            displayName: unknownField.submittedFieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: `Field created from ${unknownField.submissionCount} submission${unknownField.submissionCount > 1 ? 's' : ''} with ${unknownField.submittedValues.length} unique value${unknownField.submittedValues.length > 1 ? 's' : ''}`,
            dataType: 'string',
            publish: true,
            visible: true,
            required: false,
            fieldType: suggestedFieldType,
            aliases: [unknownField.submittedFieldName],
            units: null,
            options: suggestedFieldType === 'fixed' ? unknownField.submittedValues : null
        });
        
        setEditingField(null);
        setShowMappingDialog(false);
        setShowNewFieldDialog(true);
    };

    const handleMapToExistingField = async (targetField: ApiFieldDefinition) => {
        if (!selectedUnknownField) return;

        try {
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (token) {
                const response = await fetch('/api/v1/admin/add-field-alias', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        aliasName: selectedUnknownField.submittedFieldName,
                        targetFieldName: targetField.apiFieldName
                    })
                });

                if (response.ok) {
                    // Update field in the UI to show the new alias
                    const updatedFields = fieldDefinitions.map(f => 
                        f.id === targetField.id 
                            ? { ...f, aliases: [...f.aliases, selectedUnknownField.submittedFieldName], updatedAt: new Date() }
                            : f
                    );
                    setFieldDefinitions(updatedFields);
                    
                    // Remove from unknown fields
                    setUnknownFields(unknownFields.filter(uf => uf.id !== selectedUnknownField.id));
                    setShowMappingDialog(false);
                    setSelectedUnknownField(null);
                    
                    console.log(`🔗 Alias "${selectedUnknownField.submittedFieldName}" added to field "${targetField.displayName || targetField.fieldName}"`);
                } else {
                    const error = await response.json();
                    console.error('Failed to add alias:', error.error);
                    alert(`Failed to add alias: ${error.error}`);
                }
            } else {
                throw new Error('Authentication token not available');
            }
        } catch (error) {
            console.error('Failed to add alias:', error);
            alert(`Failed to add alias: ${error.message}`);
        }
    };

    const toggleFieldVisibility = (field: ApiFieldDefinition) => {
        const updatedFields = fieldDefinitions.map(f => 
            f.id === field.id ? { ...f, visible: !f.visible, updatedAt: new Date() } : f
        );
        setFieldDefinitions(updatedFields);
    };

    const handleDeprecateField = async (field: ApiFieldDefinition) => {
        if (!confirm(`Are you sure you want to deprecate the field "${field.displayName || field.fieldName}"?\n\nThis will prevent new API submissions from using this field, but existing data will be preserved.`)) {
            return;
        }

        try {
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (token) {
                const response = await fetch('/api/v1/admin/deprecate-field', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ fieldName: field.apiFieldName })
                });

                if (response.ok) {
                    // Update field in the UI to show as deprecated
                    const updatedFields = fieldDefinitions.map(f => 
                        f.id === field.id ? { 
                            ...f, 
                            publish: false, 
                            visible: false,
                            updatedAt: new Date() 
                        } : f
                    );
                    setFieldDefinitions(updatedFields);
                    console.log(`⚠️ Field "${field.displayName || field.fieldName}" deprecated successfully`);
                } else {
                    const error = await response.json();
                    console.error('Failed to deprecate field:', error.error);
                    alert(`Failed to deprecate field: ${error.error}`);
                }
            } else {
                throw new Error('Authentication token not available');
            }
        } catch (error) {
            console.error('Failed to deprecate field:', error);
            alert(`Failed to deprecate field: ${error.message}`);
        }
    };

    const handleClearFieldData = async () => {
        if (!confirm('Are you sure you want to clear all unknown field data?\n\nThis will remove all unknown fields from the tracking list. This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch('/api/v1/admin/reset-field-mapping', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Clear unknown fields from the UI
                setUnknownFields([]);
                console.log(`🔄 Field mapping data cleared successfully`);
                alert('Unknown field data cleared successfully.');
            } else {
                const error = await response.json();
                console.error('Failed to clear field data:', error.error);
                alert(`Failed to clear field data: ${error.error}`);
            }
        } catch (error) {
            console.error('Failed to clear field data:', error);
            alert(`Failed to clear field data: ${error.message}`);
        }
    };

    const handleApproveOption = async (unknownField: UnknownFieldSubmission) => {
        console.log('🔍 APPROVE DEBUG: unknownField data:', unknownField);
        
        if (!unknownField.fieldName || !unknownField.optionValue) {
            console.error('❌ APPROVE ERROR: Missing field data', {
                fieldName: unknownField.fieldName,
                optionValue: unknownField.optionValue,
                type: unknownField.type
            });
            alert('Error: Missing field name or option value');
            return;
        }

        if (!confirm(`Approve "${unknownField.optionValue}" as a new option for field "${unknownField.fieldName}"?\n\nThis will add it to the field's available options list.`)) {
            console.log('🚫 APPROVE: User cancelled approval');
            return;
        }
        
        console.log('✅ APPROVE: Starting approval process...');

        try {
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            console.log('🔑 APPROVE: Got auth token:', token ? 'Yes' : 'No');
            
            if (token) {
                const requestData = {
                    fieldName: unknownField.fieldName,
                    optionValue: unknownField.optionValue
                };
                console.log('📤 APPROVE: Sending request:', requestData);
                
                const response = await fetch('/api/v1/admin/approve-option', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestData)
                });
                
                console.log('📥 APPROVE: Response status:', response.status, response.statusText);

                if (response.ok) {
                    const result = await response.json();
                    console.log('✅ APPROVE SUCCESS:', result);
                    // Remove from unknown fields and refresh field definitions
                    setUnknownFields(unknownFields.filter(uf => uf.id !== unknownField.id));
                    loadData(); // Reload to get updated field definitions
                    console.log(`✅ Option "${unknownField.optionValue}" approved for field "${unknownField.fieldName}"`);
                    alert(`Successfully approved option "${unknownField.optionValue}" for field "${unknownField.fieldName}"`);
                } else {
                    const errorText = await response.text();
                    console.error('❌ APPROVE ERROR: Response not OK:', response.status, errorText);
                    try {
                        const error = JSON.parse(errorText);
                        alert(`Failed to approve option: ${error.error || error.message}`);
                    } catch {
                        alert(`Failed to approve option: ${response.status} ${response.statusText}`);
                    }
                }
            } else {
                throw new Error('Authentication token not available');
            }
        } catch (error) {
            console.error('Failed to approve option:', error);
            alert(`Failed to approve option: ${error.message}`);
        }
    };

    const handleRejectOption = async (unknownField: UnknownFieldSubmission) => {
        console.log('🔍 REJECT DEBUG: unknownField data:', unknownField);
        
        if (!unknownField.fieldName || !unknownField.optionValue) {
            console.error('❌ REJECT ERROR: Missing field data', {
                fieldName: unknownField.fieldName,
                optionValue: unknownField.optionValue,
                type: unknownField.type
            });
            alert('Error: Missing field name or option value');
            return;
        }

        if (!confirm(`Reject "${unknownField.optionValue}" as an option for field "${unknownField.fieldName}"?\n\nThis will remove it from the pending options list.`)) {
            console.log('🚫 REJECT: User cancelled rejection');
            return;
        }
        
        console.log('✅ REJECT: Starting rejection process...');

        try {
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            console.log('🔑 REJECT: Got auth token:', token ? 'Yes' : 'No');
            
            if (token) {
                const requestData = {
                    fieldName: unknownField.fieldName,
                    optionValue: unknownField.optionValue
                };
                console.log('📤 REJECT: Sending request:', requestData);
                
                const response = await fetch('/api/v1/admin/reject-option', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestData)
                });
                
                console.log('📥 REJECT: Response status:', response.status, response.statusText);

                if (response.ok) {
                    const result = await response.json();
                    console.log('❌ REJECT SUCCESS:', result);
                    // Remove from unknown fields
                    setUnknownFields(unknownFields.filter(uf => uf.id !== unknownField.id));
                    console.log(`❌ Option "${unknownField.optionValue}" rejected for field "${unknownField.fieldName}"`);
                    alert(`Successfully rejected option "${unknownField.optionValue}" for field "${unknownField.fieldName}"`);
                } else {
                    const errorText = await response.text();
                    console.error('❌ REJECT ERROR: Response not OK:', response.status, errorText);
                    try {
                        const error = JSON.parse(errorText);
                        alert(`Failed to reject option: ${error.error || error.message}`);
                    } catch {
                        alert(`Failed to reject option: ${response.status} ${response.statusText}`);
                    }
                }
            } else {
                throw new Error('Authentication token not available');
            }
        } catch (error) {
            console.error('Failed to reject option:', error);
            alert(`Failed to reject option: ${error.message}`);
        }
    };

    const handleRemoveUnknownField = async (unknownField: UnknownFieldSubmission) => {
        if (!confirm(`Remove "${unknownField.submittedFieldName}" from the unknown fields list?\n\nThis will hide it from the list but preserve the data in existing quotes. If this field is submitted again, it will reappear in the list.`)) {
            return;
        }

        try {
            const response = await fetch('/api/v1/admin/remove-unknown-field', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ fieldName: unknownField.submittedFieldName })
            });

            if (response.ok) {
                // Remove from UI immediately
                setUnknownFields(unknownFields.filter(uf => uf.id !== unknownField.id));
                console.log(`🗑️ Removed unknown field "${unknownField.submittedFieldName}" from tracking list`);
            } else {
                const error = await response.json();
                console.error('Failed to remove unknown field:', error.error);
                alert(`Failed to remove field: ${error.error}`);
            }
        } catch (error) {
            console.error('Failed to remove unknown field:', error);
            alert(`Failed to remove field: ${error.message}`);
        }
    };

    const getFieldCounts = () => {
        const total = fieldDefinitions.length;
        const visible = fieldDefinitions.filter(f => f.visible).length;
        const published = fieldDefinitions.filter(f => f.publish).length;
        const required = fieldDefinitions.filter(f => f.required).length;
        
        return { total, visible, published, required };
    };

    const fieldCounts = getFieldCounts();

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>Loading field mapping interface...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                        <Typography variant="h5">Field Mapping Administration</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage API field definitions, aliases, and unknown field mappings
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Stack direction="row" spacing={1}>
                            <Chip label={`${fieldCounts.total} Total`} size="small" />
                            <Chip label={`${fieldCounts.visible} Visible`} size="small" color="success" />
                            <Chip label={`${fieldCounts.published} Published`} size="small" color="primary" />
                            <Chip label={`${fieldCounts.required} Required`} size="small" color="error" />
                        </Stack>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<BlockIcon />}
                            onClick={handleClearFieldData}
                            size="small"
                        >
                            Clear Unknown Fields
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShowNewFieldDialog(true)}
                        >
                            New Field
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Field Definitions" />
                    <Tab 
                        label={
                            <Badge badgeContent={unknownFields.length} color="error">
                                Unknown Fields
                            </Badge>
                        }
                    />
                    <Tab label="Field Aliases" />
                </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Field Name</TableCell>
                                <TableCell>API Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Options</TableCell>
                                <TableCell>Aliases</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fieldDefinitions.map((field) => (
                                <TableRow key={field.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={field.required ? 'bold' : 'normal'}>
                                            {field.displayName || field.fieldName}
                                            {field.required && (
                                                <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" component="code" 
                                            sx={{ backgroundColor: 'grey.100', px: 1, borderRadius: 1 }}>
                                            {field.apiFieldName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{field.dataType}</TableCell>
                                    <TableCell>
                                        {field.fieldType === 'fixed' ? (
                                            field.options && field.options.length > 0 ? (
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                        {field.options.length === 1 && field.options[0] === 'Currently not available' 
                                                            ? 'Currently not available'
                                                            : `${field.options.length} options`
                                                        }
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem', opacity: 0.7 }}>
                                                        {field.options.length > 3 
                                                            ? `${field.options.slice(0, 3).join(', ')}...`
                                                            : field.options.join(', ')
                                                        }
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: 'warning.main' }}>
                                                    No options defined
                                                </Typography>
                                            )
                                        ) : (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Variable field
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {field.aliases.map((alias) => (
                                            <Chip key={alias} label={alias} size="small" 
                                                variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {!field.publish ? (
                                                <Chip label="Deprecated" size="small" color="error" />
                                            ) : (
                                                <Chip label="Published" size="small" color="primary" />
                                            )}
                                            {field.visible && <Chip label="Visible" size="small" color="success" />}
                                            {!field.visible && field.publish && <Chip label="Hidden" size="small" color="default" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleEditField(field)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => toggleFieldVisibility(field)}
                                            color={field.visible ? 'success' : 'default'}
                                        >
                                            {field.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                        </IconButton>
                                        <Tooltip title="Deprecate field (prevents new submissions)">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleDeprecateField(field)}
                                                color="error"
                                                disabled={!field.publish} // Already deprecated fields are disabled
                                            >
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    These fields were submitted via the API but don't have defined mappings. 
                    Map them to existing fields or create new field definitions.
                </Alert>
                
                {unknownFields.map((unknownField) => (
                    <Card key={unknownField.id} sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={3}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {unknownField.type === 'newOption' ? unknownField.fieldName : unknownField.submittedFieldName}
                                        </Typography>
                                        {unknownField.type === 'newOption' ? (
                                            <Chip label="New Option" size="small" color="warning" />
                                        ) : (
                                            <Chip label="Unknown Field" size="small" color="error" />
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            Submitted {unknownField.submissionCount} times
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body2">
                                        <strong>{unknownField.type === 'newOption' ? 'New Option:' : 'Value:'}</strong> {unknownField.type === 'newOption' ? unknownField.optionValue : unknownField.submittedValue}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body2">
                                        <strong>Quote:</strong> {unknownField.quoteId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Last: {new Date(unknownField.lastSubmitted).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {unknownField.type === 'newOption' ? (
                                            <>
                                                <Tooltip title="Approve new option">
                                                    <IconButton
                                                        color="success"
                                                        onClick={() => handleApproveOption(unknownField)}
                                                        sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } }}
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject new option">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRejectOption(unknownField)}
                                                        sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                    >
                                                        <BlockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleMapUnknownField(unknownField)}
                                                    startIcon={<LinkIcon />}
                                                >
                                                    Map Field
                                                </Button>
                                                <Tooltip title="Remove from unknown fields list">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveUnknownField(unknownField)}
                                                    >
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <Typography variant="body1" paragraph>
                    Field aliases allow the API to accept multiple field names that map to the same official field.
                    This supports field evolution and legacy system integration.
                </Typography>
                
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Alias Name</TableCell>
                                <TableCell>Maps to API Field</TableCell>
                                <TableCell>Field Display Name</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fieldDefinitions.flatMap(field => 
                                field.aliases.map((alias) => ({
                                    alias,
                                    field,
                                    key: `${field.id}-${alias}`
                                }))
                            ).map(({ alias, field, key }) => (
                                <TableRow key={key}>
                                    <TableCell>
                                        <Typography variant="body2" component="code" 
                                            sx={{ backgroundColor: 'grey.100', px: 1, borderRadius: 1 }}>
                                            {alias}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" component="code" 
                                            sx={{ backgroundColor: 'primary.main', color: 'white', px: 1, borderRadius: 1 }}>
                                            {field.apiFieldName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{field.displayName || field.fieldName}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label="Active" 
                                            size="small" 
                                            color="success"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            {/* New/Edit Field Dialog */}
            <Dialog open={showNewFieldDialog} onClose={() => setShowNewFieldDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingField ? 'Edit Field Definition' : selectedUnknownField ? `Create Field from "${selectedUnknownField.submittedFieldName}"` : 'Create New Field Definition'}
                </DialogTitle>
                <DialogContent>
                    {selectedUnknownField && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Creating field from unknown submission:</strong>
                            </Typography>
                            <Typography variant="body2">
                                • Submitted <strong>{selectedUnknownField.submissionCount}</strong> times
                            </Typography>
                            <Typography variant="body2">
                                • Unique values: <strong>{selectedUnknownField.submittedValues.join(', ')}</strong>
                            </Typography>
                            <Typography variant="body2">
                                • Used in quotes: <strong>{selectedUnknownField.quoteIds.length}</strong>
                            </Typography>
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Field Name"
                                value={fieldForm.fieldName}
                                onChange={(e) => setFieldForm({ ...fieldForm, fieldName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="API Field Name"
                                value={fieldForm.apiFieldName}
                                onChange={(e) => setFieldForm({ ...fieldForm, apiFieldName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Display Name"
                                value={fieldForm.displayName}
                                onChange={(e) => setFieldForm({ ...fieldForm, displayName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Description"
                                value={fieldForm.description}
                                onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                    value={fieldForm.dataType}
                                    label="Data Type"
                                    onChange={(e) => setFieldForm({ ...fieldForm, dataType: e.target.value })}
                                >
                                    <MenuItem value="string">String</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="integer">Integer</MenuItem>
                                    <MenuItem value="decimal">Decimal</MenuItem>
                                    <MenuItem value="boolean">Boolean</MenuItem>
                                    <MenuItem value="text">Text</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Field Type</InputLabel>
                                <Select
                                    value={fieldForm.fieldType}
                                    label="Field Type"
                                    onChange={(e) => setFieldForm({ ...fieldForm, fieldType: e.target.value as 'variable' | 'fixed' })}
                                >
                                    <MenuItem value="variable">Variable</MenuItem>
                                    <MenuItem value="fixed">Fixed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {fieldForm.fieldType === 'fixed' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Fixed Options (one per line)"
                                    value={optionsText}
                                    onChange={(e) => setOptionsText(e.target.value)}
                                    helperText="Enter each option on a new line. These will be the allowed values for this field."
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fieldForm.required}
                                                onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                                            />
                                        }
                                        label="Required"
                                    />
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fieldForm.publish}
                                                onChange={(e) => setFieldForm({ ...fieldForm, publish: e.target.checked })}
                                            />
                                        }
                                        label="Publish to API"
                                    />
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fieldForm.visible}
                                                onChange={(e) => setFieldForm({ ...fieldForm, visible: e.target.checked })}
                                            />
                                        }
                                        label="Visible in Documentation"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShowNewFieldDialog(false); resetForm(); setSelectedUnknownField(null); }}>Cancel</Button>
                    <Button onClick={handleSaveField} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Field Mapping Dialog */}
            <Dialog open={showMappingDialog} onClose={() => setShowMappingDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Map Unknown Field: {selectedUnknownField?.submittedFieldName}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        Choose how to handle this unknown field:
                    </Typography>
                    
                    <Stack spacing={2}>
                        <Button 
                            variant="outlined" 
                            onClick={handleCreateNewFieldFromUnknown}
                            startIcon={<AddIcon />}
                        >
                            Create New Field Definition
                        </Button>
                        
                        <Divider>OR</Divider>
                        
                        <Typography variant="body2">Map to existing field:</Typography>
                        <Autocomplete
                            options={fieldDefinitions}
                            getOptionLabel={(option) => `${option.apiFieldName} (${option.displayName || option.fieldName})`}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body2">{option.apiFieldName}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.displayName || option.fieldName}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            onChange={(event, value) => value && handleMapToExistingField(value)}
                            renderInput={(params) => <TextField {...params} label="Select existing field" />}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMappingDialog(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};